import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { CalendarSubscriptionDetail } from '../types';

export function useCalendarSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<CalendarSubscriptionDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const { subscriptions: data } = await api.listCalendarSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('[calendar subscriptions] Failed to fetch', err);
      toast({
        title: 'Failed to load subscriptions',
        description: err instanceof Error ? err.message : 'Could not load calendar subscriptions',
        variant: 'destructive',
      });
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const subscribe = useCallback(async (feedUrl: string, name?: string) => {
    const result = await api.subscribeToCalendarFeed({ feedUrl, name });
    setSubscriptions((prev) => [result, ...prev]);
    return result;
  }, []);

  const refresh = useCallback(async (id: string) => {
    const result = await api.refreshCalendarSubscription(id);
    setSubscriptions((prev) => prev.map((s) => (s.id === id ? result : s)));
    return result;
  }, []);

  const unsubscribe = useCallback(async (id: string) => {
    await api.unsubscribeCalendar(id);
    setSubscriptions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return {
    subscriptions,
    loading,
    fetchSubscriptions,
    subscribe,
    refresh,
    unsubscribe,
  };
}
